import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronDown } from "react-icons/fa";
import http from "../api/apiClient";
import { mapHttpErrorToMessage } from "../utils/mapHttpErrorToMessage";

export interface IssueOwner {
  sub: string;
  name: string;
  email?: string | null;
}

interface GiteaIssueOwnerSelectProps {
  issueNumber?: string | number;
  ownerName: string;
  ownerEmail?: string | null;
  /** Called with the new owner after the backend saved it. */
  onChanged: (owner: IssueOwner) => void;
}

/** The backend's own message when it gave one (such as a conflict), else the usual message for the status. */
const errorText = (error: any, t: any, fallback: string): string => {
  const serverMessage = error?.response?.data?.message;
  if (typeof serverMessage === "string" && serverMessage.trim()) return serverMessage.trim();
  return mapHttpErrorToMessage(error, t)?.message || fallback;
};

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";

/**
 * The owner (reporter) of an issue as a compact button that opens a searchable list of Lumen users.
 * Choosing one saves it at once; on failure the current owner stays and the error is shown below.
 */
export function GiteaIssueOwnerSelect({ issueNumber, ownerName, ownerEmail, onChanged }: GiteaIssueOwnerSelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [candidates, setCandidates] = useState<IssueOwner[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await http.get("/admin/gitea/issues/owner-candidates", { params: { query: query.trim(), limit: 20 } });
        if (active) setCandidates(Array.isArray(res?.data) ? res.data : []);
      } catch (e) {
        if (active) setLoadError(errorText(e, t, t("gitea.owner.load_failed", { defaultValue: "Users could not be loaded." })));
      } finally {
        if (active) setLoading(false);
      }
    }, 250);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [open, query, t]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", closeOutside);
    return () => document.removeEventListener("mousedown", closeOutside);
  }, [open]);

  const choose = async (owner: IssueOwner) => {
    setOpen(false);
    if (issueNumber == null) { onChanged(owner); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await http.put(`/admin/gitea/issues/${encodeURIComponent(String(issueNumber))}/owner`, { ownerSub: owner.sub });
      onChanged(res?.data?.owner || owner);
    } catch (e) {
      setError(errorText(e, t, t("gitea.owner.save_failed", { defaultValue: "The owner could not be changed." })));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div ref={rootRef} className="relative inline-flex flex-col">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("gitea.owner.change", { defaultValue: "Change owner: {{name}}", name: ownerName })}
        disabled={saving}
        onClick={() => { setQuery(""); setOpen((value) => !value); }}
        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2 py-0.5 text-sm font-medium text-gray-800 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500/60 disabled:cursor-wait disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
      >
        <span>{saving ? t("gitea.owner.saving", { defaultValue: "Saving…" }) : ownerName}</span>
        {!saving && ownerEmail ? <span className="text-gray-500 dark:text-gray-400">({ownerEmail})</span> : null}
        <FaChevronDown aria-hidden="true" className="h-3 w-3 text-gray-400" />
      </button>
      {error && <p role="alert" className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
      {open && (
        <div
          className="absolute left-0 top-full z-30 mt-1 w-72 rounded-xl border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-700 dark:bg-gray-900"
          onKeyDown={(event) => { if (event.key === "Escape") setOpen(false); }}
        >
          <input
            type="search"
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("gitea.owner.search", { defaultValue: "Search users" })}
            aria-label={t("gitea.owner.search", { defaultValue: "Search users" })}
            className="w-full rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/60 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
          />
          <ul role="listbox" aria-label={t("gitea.owner.users", { defaultValue: "Users" })} className="mt-2 max-h-64 overflow-y-auto">
            {loading && <li className="px-2 py-1.5 text-sm text-gray-500">{t("gitea.owner.loading", { defaultValue: "Loading users…" })}</li>}
            {!loading && loadError && <li role="alert" className="px-2 py-1.5 text-sm text-red-600 dark:text-red-400">{loadError}</li>}
            {!loading && !loadError && candidates.length === 0 && (
              <li className="px-2 py-1.5 text-sm text-gray-500">{t("gitea.owner.no_results", { defaultValue: "No users found" })}</li>
            )}
            {!loading && !loadError && candidates.map((candidate) => (
              <li key={candidate.sub}>
                <button
                  type="button"
                  role="option"
                  aria-selected={candidate.name === ownerName}
                  onClick={() => void choose(candidate)}
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none dark:hover:bg-gray-800 dark:focus:bg-gray-800"
                >
                  <span aria-hidden="true" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-semibold text-indigo-700 dark:bg-indigo-950 dark:text-indigo-200">
                    {initials(candidate.name)}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-gray-900 dark:text-gray-100">{candidate.name}</span>
                    {candidate.email && <span className="block truncate text-xs text-gray-500 dark:text-gray-400">{candidate.email}</span>}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
